import { Body_add_your_food_api_v1_meals_your_food_post } from "@/clients/models/Body_add_your_food_api_v1_meals_your_food_post";
import { useAddYourFood } from "@/hooks/requests/your-food/use-add-your-food";
import { useToast } from "@/hooks/ui/use-toast";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { useIonRouter } from "@ionic/react";
import { useCallback, useState } from "react";

const isCameraCancel = (error: unknown) => {
  if (!error) {
    return false;
  }
  const message = error instanceof Error ? error.message : String(error);
  return message.toLowerCase().includes("cancel");
};

const base64ToBlob = (base64: string, contentType: string): Blob => {
  const byteCharacters = atob(base64);
  const byteArrays: Uint8Array[] = [];
  const sliceSize = 512;
  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);
    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i += 1) {
      byteNumbers[i] = slice.charCodeAt(i);
    }
    byteArrays.push(new Uint8Array(byteNumbers));
  }
  return new Blob(byteArrays as BlobPart[], { type: contentType });
};

export const useTakeRecipePhoto = () => {
  const router = useIonRouter();
  const { addYourFoodAsync, isLoading } = useAddYourFood();
  const { showError, showSuccess } = useToast();
  const [capturedPhoto, setCapturedPhoto] = useState<File | null>(null);
  const takePhoto = useCallback(async () => {
    if (isLoading) {
      return;
    }

    try {
      const result = await Camera.requestPermissions({
        permissions: ["camera"],
      });
      if (result.camera !== "granted") {
        showError("Please allow camera access in Settings to continue.");
        return;
      }

      const photo = await Camera.getPhoto({
        quality: 85,
        allowEditing: false,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera,
      });

      if (!photo.base64String) {
        showError("Unable to access the captured photo.");
        return;
      }

      const imageBlob = base64ToBlob(
        photo.base64String,
        photo.format ? `image/${photo.format}` : "image/jpeg",
      );
      const imageFile = new File([imageBlob], "recipe.jpg", {
        type: imageBlob.type || "image/jpeg",
      });

      // Store the captured photo and suggested meal time
      setCapturedPhoto(imageFile);
    } catch (error) {
      if (isCameraCancel(error)) {
        return;
      }
      console.error("Camera access failed", error);
      showError("Camera access failed. Please enable permissions and retry.");
    }
  }, [isLoading, showError]);

  const uploadPhoto = useCallback(
    async (
      mealTime: Body_add_your_food_api_v1_meals_your_food_post.meal_time,
      buildProcessingPath?: (mealId: string) => string,
    ) => {
      if (!capturedPhoto) {
        return;
      }

      try {
        const data = await addYourFoodAsync({
          image: capturedPhoto,
          meal_time: mealTime,
        });

        showSuccess("Photo uploaded and processing.");
        setCapturedPhoto(null);
        console.log(data);
        const mealId = data.meal_id;
        const recipeProcessingPath =
          buildProcessingPath?.(mealId) ?? `/plans/recipe-processing?mealId=${mealId}`;
        router.push(recipeProcessingPath, "forward", "replace");
        return mealId;
      } catch (error) {
        if (error instanceof Error) {
          showError(JSON.stringify(error));
        } else {
          showError("Failed to upload photo");
        }
        return null;
      }
    },
    [router, capturedPhoto, addYourFoodAsync, showSuccess, showError],
  );

  const abandonPhoto = useCallback(() => {
    setCapturedPhoto(null);
  }, []);

  return {
    takePhoto,
    uploadPhoto,
    abandonPhoto,
    capturedPhoto,
    isLoading,
  };
};
